import { Box, Chip, Grid, Typography } from "@mui/material";
import useColors from "../../../../../../api/hooks/useColors";
import CustomCopyButton from "../../../../../../components/CustomCopyButton";
import ExternalLink from "../../../../../../components/ExternalLink";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import { DataStoreResourceType } from "../../../../../../utils/constants";
import IntegrationImage from "../../../../integrations/components/IntegrationImage";
import { publicAccessOptionDisplayName } from "../../../filters/datastoreFilters";
import { logos, severities } from "../../../icons/iconExport";
import { DataStoreResourceTypeIconsAndLabels } from "../../../utils";
import TextIntegrationStyle from "../../TextIntegrationStyle";
import AzureBlobContainerDetailsView from "./AzureBlobContainerDetailsView";
import AzureFileShareDetailsView from "./AzureFileShareDetailsView";
import AzureQueueDetailsView from "./AzureQueueDetailsView";
import AzureSQLDatabaseDetailsView from "./AzureSQLDatabaseDetailsView";
import AzureStorageAccountDetailsView from "./AzureStorageAccountDetailsView";
import AzureTableDetailsView from "./AzureTableDetailsView";
import CheckmarxRepositoryDetailsView from "./CheckmarxRepositoryDetailsView";
import GitHubRepositoryDetailsView from "./GitHubRepositoryDetailsView";
import StarburstSnowflakeCatalogDetailsView from "./StarburstSnowflakeCatalogDetailsView";

export const getS3BucketPublicURL = (entityData) => {
  const bucketName = entityData?.display_name;
  const region = entityData?.aws_data?.region || "us-east-1";
  if (!bucketName || !region) {
    return null;
  }
  const xmlUrl = `https://${bucketName}.s3.${region}.amazonaws.com`;
  return xmlUrl;
};

const DataStoreDetailsView = ({ entityData }) => {
  const colors = useColors();

  const datastoreTypeLogo = (type) => {
    if (Object.keys(DataStoreResourceTypeIconsAndLabels).includes(type)) {
      const [Icon] = DataStoreResourceTypeIconsAndLabels[type];
      return Icon;
    }
    return null;
  };

  const DigLabelToColor = {
    PII: colors.dig_label_light_blue,
    Financial: colors.dig_label_purple,
    "Developer Secrets": colors.dig_label_brown,
    PCI: colors.dig_label_pink,
    Sensitive: colors.dig_label_turquoise,
    default: colors.grey_400,
  };

  if (entityData?.resource_type === DataStoreResourceType.AZURE_SQL_DB) {
    return <AzureSQLDatabaseDetailsView entityData={entityData} />;
  } else if (
    entityData?.resource_type === DataStoreResourceType.AZURE_STORAGE_ACCOUNT
  ) {
    return <AzureStorageAccountDetailsView entityData={entityData} />;
  } else if (
    entityData?.resource_type === DataStoreResourceType.AZURE_BLOB_CONTAINER
  ) {
    return <AzureBlobContainerDetailsView entityData={entityData} />;
  } else if (
    entityData?.resource_type === DataStoreResourceType.AZURE_FILE_SHARE
  ) {
    return <AzureFileShareDetailsView entityData={entityData} />;
  } else if (entityData?.resource_type === DataStoreResourceType.AZURE_QUEUE) {
    return <AzureQueueDetailsView entityData={entityData} />;
  } else if (entityData?.resource_type === DataStoreResourceType.AZURE_TABLE) {
    return <AzureTableDetailsView entityData={entityData} />;
  } else if (
    entityData?.resource_type ===
    DataStoreResourceType.STARBURST_SNOWFLAKE_CATALOG
  ) {
    return <StarburstSnowflakeCatalogDetailsView entityData={entityData} />;
  } else if (entityData?.resource_type === DataStoreResourceType.REPOSITORY) {
    if (entityData?.github_data) {
      return <GitHubRepositoryDetailsView entityData={entityData} />;
    } else if (entityData?.checkmarx_data) {
      return <CheckmarxRepositoryDetailsView entityData={entityData} />;
    } else {
      return <></>;
    }
  } else {
    return (
      <Box display={"flex"} flexDirection={"column"} gap={2}>
        {entityData?.aws_data?.account_nickname && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Account Name</Typography>
            </Box>
            <Box ml={2} display={"flex"}>
              {entityData?.aws_data?.account_nickname && (
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.aws_data?.account_nickname}
                ></TextIntegrationStyle>
              )}
            </Box>
          </Box>
        )}
        {entityData?.mongo_atlas_data?.project_name && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Project Name</Typography>
            </Box>
            <Box ml={2} display={"flex"}>
              {entityData?.mongo_atlas_data?.project_name && (
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.mongo_atlas_data?.project_name}
                ></TextIntegrationStyle>
              )}
            </Box>
          </Box>
        )}
        {(entityData?.aws_data?.account_id ||
          entityData?.sentra_data?.account_id) && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Account ID</Typography>
            </Box>
            {entityData?.aws_data?.account_id && (
              <Box ml={2} display={"flex"} gap={2}>
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.aws_data?.account_id}
                ></TextIntegrationStyle>
                <CustomCopyButton
                  copyTitle={"Account ID"}
                  copyText={entityData?.aws_data?.account_id}
                />
              </Box>
            )}
            {entityData?.sentra_data?.account_id &&
              !entityData?.aws_data?.account_id && (
                <Box ml={2} display={"flex"} gap={2}>
                  <TextIntegrationStyle
                    title={""}
                    value={entityData?.sentra_data?.account_id}
                  ></TextIntegrationStyle>
                  <CustomCopyButton
                    copyTitle={"Account ID"}
                    copyText={entityData?.sentra_data?.account_id}
                  />
                </Box>
              )}
          </Box>
        )}
        {entityData?.aws_data?.org && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <Typography variant={"subtitle2"}>Organization</Typography>
            </Box>
            <Box display={"flex"} gap={2} ml={2}>
              {entityData?.aws_data?.org && (
                <IntegrationImage name={"aws"} width={"30px"} />
              )}
              <TextIntegrationStyle
                title={""}
                value={entityData?.aws_data?.org}
              ></TextIntegrationStyle>
            </Box>
          </Box>
        )}
        {entityData?.mongo_atlas_data?.org_name && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <Typography variant={"subtitle2"}>Organization</Typography>
            </Box>
            <Box display={"flex"} gap={2} ml={2}>
              {entityData?.mongo_atlas_data?.org_name && (
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.mongo_atlas_data?.org_name}
                ></TextIntegrationStyle>
              )}
            </Box>
          </Box>
        )}
        {(entityData?.resource_type ||
          entityData?.mongo_atlas_data?.cluster_type ||
          entityData?.snowflake_data?.type) && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Type</Typography>
            </Box>
            <Box ml={2} display={"flex"}>
              {entityData?.resource_type && (
                <Box ml={1} display={"flex"} flexDirection={"row"}>
                  <SvgToIcon
                    IconLogo={datastoreTypeLogo(entityData?.resource_type)}
                    width={"16px"}
                  />
                  <TextIntegrationStyle
                    title={""}
                    value={entityData?.resource_type}
                  ></TextIntegrationStyle>
                </Box>
              )}
              {entityData?.mongo_atlas_data?.cluster_type && (
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.mongo_atlas_data?.cluster_type}
                ></TextIntegrationStyle>
              )}
              {entityData?.snowflake_data?.type && (
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.snowflake_data?.type}
                ></TextIntegrationStyle>
              )}
            </Box>
          </Box>
        )}
        {(entityData?.aws_data?.region || entityData?.sentra_data?.region) && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Region</Typography>
            </Box>
            {entityData?.aws_data?.region && (
              <Box ml={2} display={"flex"}>
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.aws_data?.region}
                ></TextIntegrationStyle>
              </Box>
            )}
            {entityData?.sentra_data?.region &&
              !entityData?.aws_data?.region && (
                <Box ml={2} display={"flex"}>
                  <TextIntegrationStyle
                    title={""}
                    value={entityData?.sentra_data?.region}
                  ></TextIntegrationStyle>
                </Box>
              )}
          </Box>
        )}
        {(entityData?.aws_data?.resource_arn ||
          entityData?.sentra_data?.resource_arn) && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>ARN</Typography>
            </Box>
            {entityData?.aws_data?.resource_arn && (
              <Box ml={2} display={"flex"}>
                <Grid container gap={2} direction={"row"}>
                  <Grid item xs={7}>
                    <TextIntegrationStyle
                      title={""}
                      value={entityData?.aws_data?.resource_arn}
                    ></TextIntegrationStyle>
                  </Grid>
                  <Grid item xs={3}>
                    <CustomCopyButton
                      copyTitle={"ARN"}
                      copyText={entityData?.aws_data?.resource_arn}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
            {entityData?.sentra_data?.resource_arn &&
              !entityData?.aws_data?.resource_arn && (
                <Box ml={2} display={"flex"}>
                  <Grid container gap={2} direction={"row"}>
                    <Grid item xs={7}>
                      <TextIntegrationStyle
                        title={""}
                        value={entityData?.sentra_data?.resource_arn}
                      ></TextIntegrationStyle>
                    </Grid>
                    <Grid item xs={3}>
                      <CustomCopyButton
                        copyTitle={"ARN"}
                        copyText={entityData?.sentra_data?.resource_arn}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
          </Box>
        )}
        {entityData?.sentra_data?.scan_status && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Scan Status</Typography>
            </Box>

            <Box ml={2} display={"flex"}>
              <TextIntegrationStyle
                title={""}
                value={entityData?.sentra_data?.scan_status}
              ></TextIntegrationStyle>
            </Box>
          </Box>
        )}
        {(entityData?.aws_data?.encryption_status ||
          (entityData?.mongo_atlas_data?.encryption_at_rest_provider &&
            entityData?.mongo_atlas_data?.encryption_at_rest_provider !==
              "NONE") ||
          entityData?.sentra_data?.is_encrypted !== undefined ||
          entityData?.sentra_data?.is_encrypted_in_transit !== undefined) && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Encryption</Typography>
            </Box>
            <Box ml={2} display={"flex"} flexDirection={"column"} gap={0.5}>
              {entityData?.aws_data?.encryption_status && (
                <Box ml={1} display={"flex"} flexDirection={"row"}>
                  <SvgToIcon IconLogo={logos.AWSLogo} width={"16px"} />
                  <TextIntegrationStyle
                    title={""}
                    value={entityData?.aws_data?.encryption_status}
                  ></TextIntegrationStyle>
                </Box>
              )}
              {entityData?.mongo_atlas_data?.encryption_at_rest_provider &&
                entityData?.mongo_atlas_data?.encryption_at_rest_provider !==
                  "NONE" && (
                  <Box ml={1} display={"flex"} flexDirection={"row"}>
                    <SvgToIcon IconLogo={logos.MongoAtlasLogo} width={"16px"} />
                    <TextIntegrationStyle
                      title={""}
                      value={
                        "Encryption at rest provider: " +
                        entityData?.mongo_atlas_data
                          ?.encryption_at_rest_provider
                      }
                    ></TextIntegrationStyle>
                  </Box>
                )}
              {(entityData?.sentra_data?.is_encrypted !== undefined ||
                entityData?.sentra_data?.is_encrypted_in_transit !==
                  undefined) && (
                <Box ml={1} display={"flex"} flexDirection={"row"}>
                  <SvgToIcon IconLogo={logos.SentraLogo} width={"16px"} />
                  <TextIntegrationStyle
                    title={""}
                    value={
                      (entityData?.sentra_data?.is_encrypted
                        ? "Encrypted"
                        : "Not encrypted") +
                      " " +
                      (entityData?.sentra_data?.is_encrypted_in_transit
                        ? ", encrypted in transit"
                        : ", not encrypted in transit")
                    }
                  ></TextIntegrationStyle>
                </Box>
              )}
            </Box>
          </Box>
        )}
        {(entityData?.mongo_atlas_data?.is_backup_enabled !== undefined ||
          entityData?.sentra_data?.is_backed_up !== undefined) && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Backup Enabled</Typography>
            </Box>
            <Box ml={2} display={"flex"} flexDirection={"column"} gap={0.5}>
              {entityData?.mongo_atlas_data?.is_backup_enabled !== null && (
                <Box ml={1} display={"flex"} flexDirection={"row"}>
                  <SvgToIcon IconLogo={logos.MongoAtlasLogo} width={"16px"} />
                  <TextIntegrationStyle
                    title={""}
                    value={
                      entityData?.mongo_atlas_data?.is_backup_enabled
                        ? "Yes"
                        : "No"
                    }
                  ></TextIntegrationStyle>
                </Box>
              )}
              {entityData?.sentra_data?.is_backed_up !== undefined && (
                <Box ml={1} display={"flex"} flexDirection={"row"}>
                  <SvgToIcon IconLogo={logos.SentraLogo} width={"16px"} />
                  <TextIntegrationStyle
                    title={""}
                    value={entityData?.sentra_data?.is_backed_up ? "Yes" : "No"}
                  ></TextIntegrationStyle>
                </Box>
              )}
            </Box>
          </Box>
        )}
        {/* MongoDB Atlas */}
        {entityData?.mongo_atlas_data?.is_paused && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <Typography variant={"subtitle2"}>Status</Typography>
            </Box>
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"mongoatlas"} width={"40px"} />
                  </Box>
                }
                value={entityData?.mongo_atlas_data?.is_paused && "Paused"}
              />
            </Box>
          </Box>
        )}
        {/* Dig */}
        {entityData?.dig_data?.project_id && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Project ID</Typography>
            </Box>
            <Box ml={2} display={"flex"}>
              {entityData?.dig_data?.project_id && (
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.dig_data?.project_id}
                ></TextIntegrationStyle>
              )}
            </Box>
          </Box>
        )}
        {entityData?.dig_data?.project_name && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Project Name</Typography>
            </Box>
            <Box ml={2} display={"flex"}>
              {entityData?.dig_data?.project_name && (
                <TextIntegrationStyle
                  title={""}
                  value={entityData?.dig_data?.project_name}
                ></TextIntegrationStyle>
              )}
            </Box>
          </Box>
        )}
        {entityData?.dig_data?.labels?.length > 0 && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Typography variant={"subtitle2"}>Labels</Typography>
            </Box>
            <Box ml={3} display={"flex"} flexWrap={"wrap"} gap={1}>
              {entityData.dig_data.labels.map((label, index) => (
                <Chip
                  key={index}
                  size="small"
                  label={
                    <Typography
                      variant="overline"
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {label}
                    </Typography>
                  }
                  style={{
                    color: colors.text_primary,
                    backgroundColor:
                      DigLabelToColor[label] || DigLabelToColor.default,
                    borderRadius: "6px",
                    height: "auto",
                    minHeight: "auto",
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        {entityData?.sentra_data?.is_logging_enabled !== undefined && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <Typography variant={"subtitle2"}>Logging Enabled</Typography>
            </Box>
            <Box ml={2} display={"flex"} flexDirection={"column"} gap={0.5}>
              <Box ml={1} display={"flex"} flexDirection={"row"}>
                <SvgToIcon IconLogo={logos.SentraLogo} width={"16px"} />
                <TextIntegrationStyle
                  title={""}
                  value={
                    entityData?.sentra_data?.is_logging_enabled ? "Yes" : "No"
                  }
                ></TextIntegrationStyle>
              </Box>
            </Box>
          </Box>
        )}
        {entityData?.public_permissions !== undefined &&
          entityData?.public_permissions.length > 0 && (
            <Box display={"flex"} flexDirection={"column"} gap={1}>
              <Box
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
                gap={1}
              >
                <Typography variant={"subtitle2"}>
                  Public Permissions
                </Typography>
              </Box>

              <Box ml={2} display={"flex"} marginRight={15.5}>
                <Grid container gap={2} direction={"row"} alignItems="flex-end">
                  <Grid item xs={7}>
                    <Box display={"flex"} flexDirection={"column"} gap={0.5}>
                      {entityData?.public_permissions?.map((permission) => (
                        <Box ml={1} display={"flex"} flexDirection={"row"}>
                          <SvgToIcon
                            IconLogo={severities.HighSeverityIcon}
                            width={"16px"}
                          />
                          <TextIntegrationStyle
                            key={permission}
                            title={""}
                            value={publicAccessOptionDisplayName[permission]}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <ExternalLink
                      href={getS3BucketPublicURL(entityData) || ""}
                      text=""
                      linkText={
                        <Typography
                          variant="caption"
                          color={colors.primary_main}
                        >
                          View Bucket
                        </Typography>
                      }
                      linkColor={colors.primary_main}
                      tooltipText="View S3 bucket XML listing"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
      </Box>
    );
  }
};

export default DataStoreDetailsView;
